import React from "react";
import { FiWifiOff, FiRefreshCw } from "react-icons/fi";
import logo from "./assets/hallef-logo-cropped.png";
import "./no-internet.css";

export default function NoInternet({ onRetry }) {
  const [message, setMessage] = React.useState("");
  const [checking, setChecking] = React.useState(false);

  async function retry() {
    setChecking(true);
    setMessage("");
    try {
      const connected = onRetry ? await onRetry() : await checkInternetConnection();
      if (connected) {
        if (!onRetry) window.location.assign("/");
      } else {
        setMessage("You're still offline. Check your connection and try again.");
      }
    } finally {
      setChecking(false);
    }
  }

  return (
    <main className="offline-page">
      <img className="offline-logo" src={logo} alt="Hallef Technologies" />
      <section className="offline-card" aria-labelledby="offline-title">
        <div className="offline-icon"><FiWifiOff aria-hidden="true" /></div>
        <p className="offline-eyebrow">LET'S GET YOU RECONNECTED</p>
        <h1 id="offline-title">No internet connection</h1>
        <p className="offline-description">Check your Wi-Fi or mobile data connection, then try again. We'll be here when you're back.</p>
        <button className="offline-retry" type="button" onClick={retry} disabled={checking}>
          <FiRefreshCw aria-hidden="true" /> {checking ? "Checking..." : "Try again"}
        </button>
        <p className="offline-status" role="status">{message}</p>
      </section>
      <p className="offline-footer">Hallef Technologies · Keeping you connected</p>
    </main>
  );
}

// A local development server remains reachable even when the internet is down.
// Either external endpoint succeeding is sufficient; one blocked host is tolerated.
export async function checkInternetConnection(signal) {
  if (!navigator.onLine) return false;
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) return false;
  signal?.addEventListener("abort", abort);
  const timeout = window.setTimeout(abort, 5000);
  const endpoints = [
    "https://www.gstatic.com/generate_204",
    "https://www.cloudflare.com/cdn-cgi/trace",
  ];
  try {
    await Promise.any(endpoints.map((endpoint) => fetch(`${endpoint}?connection=${Date.now()}`, {
      mode: "no-cors",
      cache: "no-store",
      credentials: "omit",
      referrerPolicy: "no-referrer",
      signal: controller.signal,
    })));
    return true;
  } catch {
    return false;
  } finally {
    window.clearTimeout(timeout);
    signal?.removeEventListener("abort", abort);
    controller.abort();
  }
}

export function ConnectionBoundary({ children }) {
  const [online, setOnline] = React.useState(
    () => typeof navigator === "undefined" || navigator.onLine,
  );

  const retryRef = React.useRef(async () => false);

  React.useEffect(() => {
    let disposed = false;
    let failures = 0;
    let currentCheck = null;
    let activeController = null;
    const check = () => {
      if (currentCheck) return currentCheck;
      const controller = new AbortController();
      activeController = controller;
      currentCheck = checkInternetConnection(controller.signal).then((connected) => {
        if (disposed || controller.signal.aborted) return false;
        failures = connected ? 0 : failures + 1;
        if (connected) setOnline(true);
        else if (!navigator.onLine || failures >= 2) setOnline(false);
        return connected;
      }).finally(() => {
        currentCheck = null;
        activeController = null;
      });
      return currentCheck;
    };
    retryRef.current = check;
    const offline = () => {
      activeController?.abort();
      failures = 2;
      setOnline(false);
    };
    const focus = () => { if (!document.hidden) check(); };
    window.addEventListener("online", check);
    window.addEventListener("offline", offline);
    window.addEventListener("focus", focus);
    document.addEventListener("visibilitychange", focus);
    check();
    const interval = window.setInterval(check, 5000);
    return () => {
      disposed = true;
      activeController?.abort();
      window.clearInterval(interval);
      window.removeEventListener("online", check);
      window.removeEventListener("offline", offline);
      window.removeEventListener("focus", focus);
      document.removeEventListener("visibilitychange", focus);
    };
  }, []);

  return (
    <>
      <div hidden={!online}>{children}</div>
      {!online && <NoInternet onRetry={() => retryRef.current()} />}
    </>
  );
}
